import {useState, useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import {SVG} from '../../../../../constants/assetLists';
import {Checkbox} from '@mui/material';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

type Permission = {
  _id: string;
  name: string;
  displayName: {
    en: string;
    he: string;
  };
  description: string;
};

type Branch = {
  _id: string;
  client_id: string;
  name: string;
  createdAt: string;
};

type Client = {
  _id: string;
  name: string;
  createdAt: string;
  branches: Branch[];
};

type GroupNode = {
  _id: string;
  name: string;
  displayName: string;
  description: string;
  parent: string | null;
  permissions: Permission[];
  children: GroupNode[];
  clients?: Client[];
};

type Props = {
  allGroupedPermissions: {data: GroupNode[]} | undefined;
};

// ---- helpers ----
function collectPermissions(group: GroupNode): string[] {
  const perms: string[] = group.permissions.map(p => p._id);
  if (group.children?.length) {
    for (const child of group.children) {
      perms.push(...collectPermissions(child));
    }
  }
  return perms;
}

function collectBranchIds(group: GroupNode): string[] {
  const ids: string[] = [];
  if (group.clients?.length) {
    for (const client of group.clients) {
      if (client.branches?.length) {
        ids.push(...client.branches.map(b => b._id));
      }
    }
  }
  if (group.children?.length) {
    for (const child of group.children) {
      ids.push(...collectBranchIds(child));
    }
  }
  return ids;
}

function collectClientIds(group: GroupNode): string[] {
  const ids: string[] = [];
  if (group.clients?.length) {
    ids.push(...group.clients.map(c => c._id));
  }
  if (group.children?.length) {
    for (const child of group.children) {
      ids.push(...collectClientIds(child));
    }
  }
  return ids;
}

function PermissionList({allGroupedPermissions}: Props) {
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState<
    'permissions' | 'clients' | null
  >(null);

  const {
    getValues,
    setValue,
    watch,
    formState: {errors},
  } = useFormContext();

  const selectedPermissions = new Set<string>(watch('permissions') ?? []);
  const selectedBranchIds = new Set<string>(watch('branchIds') ?? []);
  const selectedClientIds = new Set<string>(watch('clientIds') ?? []);

  const ALL_PERMISSIONS_ID = '691eca4365e5ec6f4985460e';
  const MOBILE_ONLY_GROUP_ID = '691ecaea65e5ec6f49854637';

  // ✅ Special parent group
  const SPECIAL_PARENT_GROUP_ID = '69158064ee1c33314eaad685';

  const collectMobileOnlyPermissionIds = (): string[] => {
    const rootGroups = allGroupedPermissions?.data ?? [];

    for (const root of rootGroups) {
      if (root.children?.length) {
        const mobileGroup = root.children.find(
          g => g._id === MOBILE_ONLY_GROUP_ID,
        );
        if (mobileGroup) {
          return collectPermissions(mobileGroup);
        }
      }
    }
    return [];
  };

  // Auto-scroll to error section and highlight it
  useEffect(() => {
    if (errors?.permissions || errors?.clientIds || errors?.branchIds) {
      const container = document.querySelector(
        '.flex-1.min-h-0.overflow-y-auto.flex.flex-col.border.border-gray80',
      );
      if (container) {
        if (errors?.clientIds || errors?.branchIds) {
          // Scroll to bottom for client/branch errors (where clients are usually located)
          // @ts-ignore
          container.scrollTo({
            top: (container as any).scrollHeight,
            behavior: 'smooth',
          });
          setHighlightedSection('clients');
        } else if (errors?.permissions) {
          // Scroll to top for permission errors
          // @ts-ignore
          container.scrollTo({top: 0, behavior: 'smooth'});
          setHighlightedSection('permissions');
        }

        // Remove highlight after 1 second
        const timer = setTimeout(() => {
          setHighlightedSection(null);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [errors?.permissions, errors?.clientIds, errors?.branchIds]);

  // Auto-expand groups and clients that contain selected branches or client
  useEffect(() => {
    if (
      !allGroupedPermissions?.data ||
      (selectedBranchIds.size === 0 && selectedClientIds.size === 0) ||
      hasAutoExpanded
    )
      return;

    const groupsToExpand = new Set<string>();
    const clientsToExpand = new Set<string>();

    const findGroupsWithSelectedBranches = (
      groups: GroupNode[],
      parentPath: string[] = [],
    ): boolean => {
      let anyChildHasSelected = false;

      for (const group of groups) {
        let hasSelectedInGroup = false;

        if (group.clients && group.clients.length > 0) {
          for (const client of group.clients) {
            const clientBranchIds = client.branches?.map(b => b._id) || [];
            const hasSelectedBranches = clientBranchIds.some(id =>
              selectedBranchIds.has(id),
            );
            const isClientSelected = selectedClientIds.has(client._id);

            if (hasSelectedBranches || isClientSelected) {
              hasSelectedInGroup = true;
              clientsToExpand.add(`client:${client._id}`);
            }
          }

          if (hasSelectedInGroup) {
            groupsToExpand.add(group._id);
            anyChildHasSelected = true;
          }
        }

        if (group.children && group.children.length > 0) {
          const childHasSelected = findGroupsWithSelectedBranches(
            group.children,
            [...parentPath, group._id],
          );
          if (childHasSelected) {
            anyChildHasSelected = true;
            groupsToExpand.add(group._id);
          }
        }
      }

      if (anyChildHasSelected) {
        parentPath.forEach(parentId => groupsToExpand.add(parentId));
      }

      return anyChildHasSelected;
    };

    findGroupsWithSelectedBranches(allGroupedPermissions.data);

    if (groupsToExpand.size > 0 || clientsToExpand.size > 0) {
      setExpandedSet(prev => {
        const next = new Set(prev);
        groupsToExpand.forEach(id => next.add(id));
        clientsToExpand.forEach(id => next.add(id));
        return next;
      });

      setHasAutoExpanded(true);
    }
  }, [
    selectedBranchIds,
    selectedClientIds,
    allGroupedPermissions,
    hasAutoExpanded,
  ]);

  const toggleExpand = (id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleTopLevelGroup = (group: GroupNode) => {
    const currentPerms = new Set<string>(getValues().permissions ?? []);
    const currentClients = new Set<string>(getValues().clientIds ?? []);
    const currentBranches = new Set<string>(getValues().branchIds ?? []);

    const rootGroups = allGroupedPermissions?.data ?? [];
    const allRootPermIds: string[] = [];
    const allRootClientIds: string[] = [];
    const allRootBranchIds: string[] = [];

    for (const root of rootGroups) {
      allRootPermIds.push(...collectPermissions(root));
      allRootClientIds.push(...collectClientIds(root));
      allRootBranchIds.push(...collectBranchIds(root));
    }

    const isSpecialParent = group._id === SPECIAL_PARENT_GROUP_ID;

    // ✅ If SPECIAL is checked only because ALL_PERMISSIONS is selected,
    // clicking SPECIAL should just turn it off (remove ALL_PERMISSIONS),
    // NOT select mobile-only permissions.
    if (isSpecialParent && currentPerms.has(ALL_PERMISSIONS_ID)) {
      currentPerms.delete(ALL_PERMISSIONS_ID);

      // defensive: ensure mobile-only perms are not accidentally left behind
      const mobileOnlyPermIds = collectMobileOnlyPermissionIds();
      mobileOnlyPermIds.forEach(id => currentPerms.delete(id));

      setValue('permissions', Array.from(currentPerms), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('clientIds', Array.from(currentClients), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('branchIds', Array.from(currentBranches), {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const groupPermIds = isSpecialParent
      ? collectMobileOnlyPermissionIds()
      : collectPermissions(group);

    const groupClientIds = isSpecialParent ? [] : collectClientIds(group);
    const groupBranchIds = isSpecialParent ? [] : collectBranchIds(group);

    const hasPerms = groupPermIds.length > 0;
    const hasClients = groupClientIds.length > 0;
    const hasBranches = groupBranchIds.length > 0;
    const hasAnyItem = hasPerms || hasClients || hasBranches;

    const allPermsSelected =
      !hasPerms || groupPermIds.every(id => currentPerms.has(id));
    const allClientsSelected =
      !hasClients || groupClientIds.every(id => currentClients.has(id));
    const allBranchesSelected =
      !hasBranches || groupBranchIds.every(id => currentBranches.has(id));

    const groupAllSelected =
      hasAnyItem &&
      allPermsSelected &&
      allClientsSelected &&
      allBranchesSelected;

    const isMobileOnlyGroup = group.name === 'Mobile only user';

    if (groupAllSelected) {
      groupPermIds.forEach(id => currentPerms.delete(id));
      groupClientIds.forEach(id => currentClients.delete(id));
      groupBranchIds.forEach(id => currentBranches.delete(id));
    } else {
      // Clear ALL root groups first (radio behavior)
      if (isMobileOnlyGroup) {
        currentPerms.delete(ALL_PERMISSIONS_ID);
      } else {
        allRootPermIds.forEach(id => currentPerms.delete(id));
        allRootClientIds.forEach(id => currentClients.delete(id));
        allRootBranchIds.forEach(id => currentBranches.delete(id));
      }

      // Then select only this group's subtree
      if (isSpecialParent) {
        // ✅ IMPORTANT: remove ALL permissions under this parent first (so its other child won't remain selected)
        const specialParentAllPermIds = collectPermissions(group);
        specialParentAllPermIds.forEach(id => currentPerms.delete(id));

        // ✅ enforce mutual exclusion (same rule as togglePermission)
        currentPerms.delete(ALL_PERMISSIONS_ID);

        // ✅ select ONLY mobile-only permissions
        const mobileOnlyIds = collectMobileOnlyPermissionIds();
        mobileOnlyIds.forEach(id => currentPerms.add(id));
      } else {
        groupPermIds.forEach(id => currentPerms.add(id));
        groupClientIds.forEach(id => currentClients.add(id));
        groupBranchIds.forEach(id => currentBranches.add(id));
      }
    }

    setValue('permissions', Array.from(currentPerms), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('clientIds', Array.from(currentClients), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('branchIds', Array.from(currentBranches), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // ---- group toggle: select/unselect all subtree (permissions + clients + branches) ----
  const toggleGroup = (group: GroupNode) => {
    const currentPerms = new Set<string>(getValues().permissions ?? []);
    const currentClients = new Set<string>(getValues().clientIds ?? []);
    const currentBranches = new Set<string>(getValues().branchIds ?? []);

    const isSpecialParent = group._id === SPECIAL_PARENT_GROUP_ID;

    // ✅ If SPECIAL is checked only because ALL_PERMISSIONS is selected,
    // clicking SPECIAL should just turn it off (remove ALL_PERMISSIONS),
    // NOT select mobile-only permissions.
    if (isSpecialParent && currentPerms.has(ALL_PERMISSIONS_ID)) {
      currentPerms.delete(ALL_PERMISSIONS_ID);

      // defensive: ensure mobile-only perms are not accidentally left behind
      const mobileOnlyPermIds = collectMobileOnlyPermissionIds();
      mobileOnlyPermIds.forEach(id => currentPerms.delete(id));

      setValue('permissions', Array.from(currentPerms), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('clientIds', Array.from(currentClients), {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('branchIds', Array.from(currentBranches), {
        shouldValidate: true,
        shouldDirty: true,
      });
      return;
    }

    const groupPermIds = isSpecialParent
      ? collectMobileOnlyPermissionIds()
      : collectPermissions(group);

    const groupClientIds = isSpecialParent ? [] : collectClientIds(group);
    const groupBranchIds = isSpecialParent ? [] : collectBranchIds(group);

    const hasPerms = groupPermIds.length > 0;
    const hasClients = groupClientIds.length > 0;
    const hasBranches = groupBranchIds.length > 0;
    const hasAnyItem = hasPerms || hasClients || hasBranches;

    const allPermsSelected =
      !hasPerms || groupPermIds.every(id => currentPerms.has(id));
    const allClientsSelected =
      !hasClients || groupClientIds.every(id => currentClients.has(id));
    const allBranchesSelected =
      !hasBranches || groupBranchIds.every(id => currentBranches.has(id));

    const groupAllSelected =
      hasAnyItem &&
      allPermsSelected &&
      allClientsSelected &&
      allBranchesSelected;

    if (groupAllSelected) {
      groupPermIds.forEach(id => currentPerms.delete(id));
      groupClientIds.forEach(id => currentClients.delete(id));
      groupBranchIds.forEach(id => currentBranches.delete(id));
    } else {
      if (isSpecialParent) {
        // ✅ IMPORTANT: remove ALL permissions under this parent first (so its other child won't remain selected)
        const specialParentAllPermIds = collectPermissions(group);
        specialParentAllPermIds.forEach(id => currentPerms.delete(id));

        // ✅ enforce mutual exclusion
        currentPerms.delete(ALL_PERMISSIONS_ID);

        // ✅ select ONLY mobile-only permissions
        const mobileOnlyIds = collectMobileOnlyPermissionIds();
        mobileOnlyIds.forEach(id => currentPerms.add(id));
      } else {
        groupPermIds.forEach(id => currentPerms.add(id));
        groupClientIds.forEach(id => currentClients.add(id));
        groupBranchIds.forEach(id => currentBranches.add(id));
      }
    }

    setValue('permissions', Array.from(currentPerms), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('clientIds', Array.from(currentClients), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('branchIds', Array.from(currentBranches), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // single permission toggle
  const togglePermission = (permId: string) => {
    const current = new Set<string>(getValues().permissions ?? []);

    const isAllPermissions = permId === ALL_PERMISSIONS_ID;
    const mobileOnlyPermIds = collectMobileOnlyPermissionIds();
    const isMobileOnlyPermission = mobileOnlyPermIds.includes(permId);

    // ---- CASE 1: selecting "all_permissions" ----
    if (isAllPermissions) {
      const willSelect = !current.has(permId);

      if (willSelect) {
        // remove ONLY Mobile-only permissions
        mobileOnlyPermIds.forEach(id => current.delete(id));
        current.add(permId);
      } else {
        current.delete(permId);
      }
    }

    // ---- CASE 2: selecting any Mobile-only permission ----
    else if (isMobileOnlyPermission) {
      const willSelect = !current.has(permId);

      if (willSelect) {
        // remove all_permissions ONLY
        current.delete(ALL_PERMISSIONS_ID);
        current.add(permId);
      } else {
        current.delete(permId);
      }
    }

    // ---- NORMAL permissions (unchanged behavior) ----
    else {
      if (current.has(permId)) current.delete(permId);
      else current.add(permId);
    }

    setValue('permissions', Array.from(current), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // single branch toggle
  const toggleBranch = (branchId: string, clientId: string) => {
    const currentBranches = new Set<string>(getValues().branchIds ?? []);
    const currentClientIds = new Set<string>(getValues().clientIds ?? []);

    if (currentBranches.has(branchId)) {
      currentBranches.delete(branchId);

      const allGroupedPermissions = getValues();
      let hasOtherBranchesForClient = false;

      if (allGroupedPermissions) {
        currentBranches.forEach(bid => {
          // Find which client this branch belongs to
          // (You need to traverse your data structure to find this)
          // For now, we'll keep the client selected
        });
      }
    } else {
      currentBranches.add(branchId);
      currentClientIds.add(clientId);
    }

    setValue('branchIds', Array.from(currentBranches), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('clientIds', Array.from(currentClientIds), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // client toggle — selects/unselects client and all its branches
  const toggleClient = (client: Client) => {
    const currentClientIds = new Set<string>(getValues().clientIds ?? []);
    const currentBranches = new Set<string>(getValues().branchIds ?? []);
    const branchIds = client.branches?.map(b => b._id) ?? [];

    const clientSelected = currentClientIds.has(client._id);
    const allBranchesSelected =
      branchIds.length > 0 && branchIds.every(id => currentBranches.has(id));

    if (clientSelected && (branchIds.length === 0 || allBranchesSelected)) {
      currentClientIds.delete(client._id);
      branchIds.forEach(id => currentBranches.delete(id));
    } else {
      currentClientIds.add(client._id);
      if (branchIds.length > 0) {
        branchIds.forEach(id => currentBranches.add(id));
      }
    }

    setValue('clientIds', Array.from(currentClientIds), {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('branchIds', Array.from(currentBranches), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // ---- recursive renderer for a group node ----
  const renderGroup = (group: GroupNode, depth: number) => {
    const expanded = expandedSet.has(group._id);
    const isTopLevel = depth === 1;

    const hasClientsInGroup = group.clients && group.clients.length > 0;
    const shouldHighlight =
      (highlightedSection === 'permissions' && !hasClientsInGroup) ||
      (highlightedSection === 'clients' && hasClientsInGroup);

    const isSpecialParent = group._id === SPECIAL_PARENT_GROUP_ID;

    const groupPermIds = isSpecialParent
      ? collectMobileOnlyPermissionIds()
      : collectPermissions(group);

    const groupClientIds = isSpecialParent ? [] : collectClientIds(group);
    const groupBranchIds = isSpecialParent ? [] : collectBranchIds(group);

    const hasPerms = groupPermIds.length > 0;
    const hasClients = groupClientIds.length > 0;
    const hasBranches = groupBranchIds.length > 0;

    const allPermsSelected =
      !hasPerms || groupPermIds.every(id => selectedPermissions.has(id));
    const allClientsSelected =
      !hasClients || groupClientIds.every(id => selectedClientIds.has(id));
    const allBranchesSelected =
      !hasBranches || groupBranchIds.every(id => selectedBranchIds.has(id));

    const hasAnyItem = hasPerms || hasClients || hasBranches;

    const groupAllSelected =
      hasAnyItem &&
      allPermsSelected &&
      allClientsSelected &&
      allBranchesSelected;

    const anyPermSelected =
      hasPerms && groupPermIds.some(id => selectedPermissions.has(id));
    const anyClientSelected =
      hasClients && groupClientIds.some(id => selectedClientIds.has(id));
    const anyBranchSelected =
      hasBranches && groupBranchIds.some(id => selectedBranchIds.has(id));

    const groupSomeSelected =
      (anyPermSelected || anyClientSelected || anyBranchSelected) &&
      !groupAllSelected;

    const paddingStart = depth === 0 ? 16 : 16 + depth * 32;

    const showRadio = group.name === 'Mobile only user';

    const anyPermInGroupSelected =
      hasPerms && groupPermIds.some(id => selectedPermissions.has(id));
    const disabledRadio = showRadio && !anyPermInGroupSelected;

    const allPermissionsSelected = selectedPermissions.has(ALL_PERMISSIONS_ID);

    // For special parent, its "real" selection is based on Mobile-only permission ids (groupPermIds)
    const specialPermIds = groupPermIds; // already equals collectMobileOnlyPermissionIds() for special parent

    const specialAllSelected =
      specialPermIds.length > 0 &&
      specialPermIds.every(id => selectedPermissions.has(id));

    const specialAnySelected =
      specialPermIds.length > 0 &&
      specialPermIds.some(id => selectedPermissions.has(id));

    // ✅ NEW requirement: if all_permissions is selected => special parent shows checked
    const uiChecked = isSpecialParent
      ? allPermissionsSelected || specialAllSelected
      : groupAllSelected;

    // ✅ Keep old indeterminate behavior, but NOT when all_permissions is selected
    const uiIndeterminate = isSpecialParent
      ? !allPermissionsSelected && specialAnySelected && !specialAllSelected
      : groupSomeSelected;

    return (
      <div key={group._id}>
        {/* Group header */}
        <div
          className={`w-full flex items-center py-2 border-b border-gray80 transition-all duration-300 ${
            shouldHighlight ? 'bg-yellow-100' : ''
          }`}
          style={{paddingInlineStart: paddingStart, paddingInlineEnd: 8}}>
          <div className="flex items-center gap-1">
            {group.children?.length ||
            group.permissions?.length ||
            group.clients?.length ? (
              <button
                type="button"
                className="w-[16px] h-[16px] flex items-center justify-center"
                onClick={() => toggleExpand(group._id)}>
                <img
                  src={expanded ? SVG.expandIcon : SVG.lessThanIcon}
                  alt="caret"
                  className="w-[12px] h-[12px] object-contain"
                />
              </button>
            ) : (
              <div className="w-[16px]" />
            )}

            {isTopLevel ? (
              <Checkbox
                color="secondary"
                key="radio"
                size="medium"
                sx={{p: 0}}
                checked={uiChecked}
                indeterminate={uiIndeterminate}
                icon={showRadio ? <RadioButtonUncheckedIcon /> : undefined}
                checkedIcon={showRadio ? <RadioButtonCheckedIcon /> : undefined}
                onClick={e => {
                  e.stopPropagation();
                  toggleTopLevelGroup(group);
                }}
              />
            ) : (
              <Checkbox
                color="secondary"
                key="checkbox"
                size="medium"
                sx={{p: 0}}
                checked={uiChecked}
                indeterminate={uiIndeterminate}
                icon={showRadio ? <RadioButtonUncheckedIcon /> : undefined}
                checkedIcon={showRadio ? <RadioButtonCheckedIcon /> : undefined}
                onChange={() => toggleGroup(group)}
              />
            )}

            <span className="text-black">
              {group.displayName || group.name}
            </span>
          </div>
        </div>

        {expanded && (
          <div>
            {/* permissions of this group */}
            {group.permissions?.map(perm => {
              const checked = selectedPermissions.has(perm._id);
              const showRadio = perm.name === 'all_permissions';
              return (
                <div
                  key={perm._id}
                  className={`w-full flex items-center py-2 border-b border-gray80 transition-all duration-300 ${
                    shouldHighlight ? 'bg-yellow-100' : ''
                  }`}
                  style={{
                    paddingInlineStart: paddingStart + 32,
                    paddingInlineEnd: 8,
                  }}>
                  <div className="flex items-center gap-1">
                    <Checkbox
                      color="secondary"
                      size="medium"
                      sx={{p: 0}}
                      checked={checked}
                      disabled={disabledRadio && !showRadio}
                      onChange={() => togglePermission(perm._id)}
                      icon={
                        showRadio ? <RadioButtonUncheckedIcon /> : undefined
                      }
                      checkedIcon={
                        showRadio ? <RadioButtonCheckedIcon /> : undefined
                      }
                    />
                    <span className="text-black">
                      {perm.displayName?.he ||
                        perm.displayName?.en ||
                        perm.name}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* clients & branches */}
            {group.clients?.map(client => {
              const clientKey = `client:${client._id}`;
              const clientExpanded = expandedSet.has(clientKey);

              const clientBranchIds = client.branches?.map(b => b._id) ?? [];
              const clientSelected = selectedClientIds.has(client._id);
              const allBranchesSelected =
                clientBranchIds.length > 0 &&
                clientBranchIds.every(id => selectedBranchIds.has(id));

              const clientAllSelected =
                clientSelected &&
                (clientBranchIds.length === 0 || allBranchesSelected);

              const clientSomeSelected =
                (clientSelected &&
                  clientBranchIds.length > 0 &&
                  !allBranchesSelected) ||
                (!clientSelected &&
                  clientBranchIds.length > 0 &&
                  clientBranchIds.some(id => selectedBranchIds.has(id)));

              return (
                <div key={client._id}>
                  <div
                    className={`w-full flex items-center py-2 border-b border-gray80 bg-gray10 transition-all duration-300 ${
                      shouldHighlight ? 'bg-yellow-100' : ''
                    }`}
                    style={{
                      paddingInlineStart: paddingStart + 32,
                      paddingInlineEnd: 8,
                    }}>
                    <div className="flex items-center gap-1">
                      {client.branches?.length > 0 ? (
                        <button
                          type="button"
                          className="w-[16px] h-[16px] flex items-center justify-center"
                          onClick={e => {
                            e.stopPropagation();
                            toggleExpand(clientKey);
                          }}>
                          <img
                            src={
                              clientExpanded ? SVG.expandIcon : SVG.lessThanIcon
                            }
                            alt="caret"
                            className="w-[12px] h-[12px] object-contain"
                          />
                        </button>
                      ) : (
                        <div className="w-[16px]" />
                      )}

                      <Checkbox
                        color="secondary"
                        size="medium"
                        sx={{p: 0}}
                        checked={clientAllSelected}
                        indeterminate={clientSomeSelected}
                        onChange={() => toggleClient(client)}
                      />

                      <span className="text-black">{client.name}</span>
                    </div>
                  </div>

                  {clientExpanded &&
                    client.branches?.map(branch => {
                      const checked = selectedBranchIds.has(branch._id);
                      return (
                        <div
                          key={branch._id}
                          className={`w-full flex items-center py-2 border-b border-gray80 transition-all duration-300 ${
                            shouldHighlight ? 'bg-yellow-100' : ''
                          }`}
                          style={{
                            paddingInlineStart: paddingStart + 65,
                            paddingInlineEnd: 8,
                          }}>
                          <div className="flex items-center gap-1">
                            <Checkbox
                              color="secondary"
                              size="medium"
                              sx={{p: 0}}
                              checked={checked}
                              onChange={() =>
                                toggleBranch(branch._id, client._id)
                              }
                            />
                            <span className="text-black">{branch.name}</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}

            {/* child groups */}
            {group.children?.map(child => renderGroup(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const totalSelectedPermissions = watch('permissions')?.length ?? 0;
  const totalSelectedClients = watch('clientIds')?.length ?? 0;
  const totalSelectedBranches = watch('branchIds')?.length ?? 0;

  return (
    <div className="flex-1 min-h-0 ms-10 flex flex-col gap-2">
      <p className="m-0">
        רשימת הרשאות (
        {totalSelectedPermissions +
          totalSelectedClients +
          totalSelectedBranches}{' '}
        בחורות)
      </p>

      {(errors?.permissions || errors?.clientIds || errors?.branchIds) && (
        <span className="text-raspberryRed text-sm">
          {(errors?.permissions?.message as string) ||
            (errors?.clientIds?.message as string) ||
            (errors?.branchIds?.message as string)}
        </span>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col border border-gray80">
        {allGroupedPermissions?.data?.map(rootGroup =>
          renderGroup(rootGroup, 0),
        )}
      </div>
    </div>
  );
}

export default PermissionList;
